/**
 * <s-product-gallery> — PDP gallery with one featured image and a thumbnail strip.
 *
 * Wraps the whole product grid so option `change` events from <s-variant-picker>
 * bubble up to it even after the product-form partial is swapped.
 *
 * Required child markup:
 *   [data-gallery-item][data-media-id]                      — one per media; all but the active one carry `hidden`.
 *   [data-gallery-thumb][data-media-id][data-variant-ids]   — thumbnail button; variant-ids is a
 *                                                             pipe-wrapped list ("|123|456|") of the
 *                                                             variants whose featured media this is.
 *
 * Optional (t-shirt template): [data-gallery-swipe] wraps the items and enables swipe and
 * arrow keys; [data-gallery-prev] / [data-gallery-next] buttons step through the thumbnails
 * in strip order, wrapping at the ends.
 */
class SProductGallery extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    // Phones pin the gallery below the sticky site header; expose its height for the template's CSS.
    const header = document.querySelector('header');
    const setTop = () => this.style.setProperty('--gallery-top', `${header ? header.getBoundingClientRect().height : 0}px`);
    setTop();
    window.addEventListener('resize', setTop, opts);

    this.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      if (event.target.closest('[data-gallery-prev]')) return this.#step(-1);
      if (event.target.closest('[data-gallery-next]')) return this.#step(1);
      const thumb = event.target.closest('[data-gallery-thumb]');
      if (!thumb || !this.contains(thumb)) return;
      this.#show(thumb.dataset.mediaId);
    }, opts);

    // Swipe: a mostly-horizontal drag of 40px+ steps the gallery and swallows the click that
    // follows, so the swipe does not also open the zoom dialog.
    let start = null;
    let swallowClick = false;
    this.addEventListener('pointerdown', (event) => {
      start = event.target instanceof Element && event.target.closest('[data-gallery-swipe]')
        ? { x: event.clientX, y: event.clientY } : null;
    }, opts);
    this.addEventListener('pointerup', (event) => {
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      start = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      swallowClick = true;
      setTimeout(() => { swallowClick = false; }, 0);
      this.#step(dx < 0 ? 1 : -1);
    }, opts);
    this.addEventListener('pointercancel', () => { start = null; }, opts);
    this.addEventListener('click', (event) => {
      if (!swallowClick) return;
      swallowClick = false;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, { ...opts, capture: true });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (!(event.target instanceof Element) || !event.target.closest('[data-gallery-swipe], [data-gallery-thumb]')) return;
      event.preventDefault();
      this.#step(event.key === 'ArrowRight' ? 1 : -1);
    }, opts);

    this.addEventListener('change', (event) => {
      const input = event.target;
      const variantId = input?.dataset?.variantId
        || input?.selectedOptions?.[0]?.dataset?.variantId;
      if (!variantId) return;
      const thumb = this.querySelector(`[data-gallery-thumb][data-variant-ids*="|${CSS.escape(variantId)}|"]`);
      if (thumb) this.#show(thumb.dataset.mediaId);
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #step(delta) {
    const thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (thumbs.length < 2) return;
    const current = thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true');
    const next = thumbs[(Math.max(current, 0) + delta + thumbs.length) % thumbs.length];
    this.#show(next.dataset.mediaId);
  }

  #show(mediaId) {
    if (!mediaId) return;
    for (const item of this.querySelectorAll('[data-gallery-item]')) {
      item.hidden = item.dataset.mediaId !== mediaId;
    }
    for (const thumb of this.querySelectorAll('[data-gallery-thumb]')) {
      const active = thumb.dataset.mediaId === mediaId;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
      if (active && typeof thumb.scrollIntoView === 'function') {
        thumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
